// ── Query params ──

type DateRangeParams = {
  start_date: string
  end_date: string
}

type ComparisonDateRangeParams = {
  first_start_date: string
  first_end_date: string
  second_start_date: string
  second_end_date: string
}

type TopUnitsParams = DateRangeParams & {
  limit?: number
}

type TopUnitsIngredientsMvParams = TopUnitsParams & {
  type_filter: string
}

type IngredientComparisonParams = ComparisonDateRangeParams & {
  type: string
}

type ReportParams = DateRangeParams & {
  language?: 'pl' | 'en'
}

type IngredientReportParams = ReportParams & {
  type: string
}

type FullReportParams = ReportParams & {
  include_value?: boolean
  include_count?: boolean
  include_avg?: boolean
  include_cum?: boolean
  include_summary?: boolean
}

type IngredientsFullReportParams = FullReportParams & {
  type: string
}

type GeneralFullReportParams = ReportParams & {
  include_total_sales?: boolean
  include_total_sales_count?: boolean
  include_aov?: boolean
  include_top_dishes?: boolean
  include_top_toppings?: boolean
  include_dish_topping_combos?: boolean
  include_top_ingredients?: boolean
}

type SalesTableParams = {
  startDate: string
  endDate: string
  limit?: number
  itemFilter?: 'dish' | 'topping' | 'danie' | 'dodatek' | null
}

type IngredientsSalesTableParams = {
  startDate: string
  endDate: string
  limit?: number
  typeFilter?:
    | 'count'
    | 'weight'
    | 'volume'
    | 'ilość'
    | 'waga'
    | 'objętość'
    | null
}

type SalesExcelExportParams = SalesTableParams & {
  language?: string
}

type IngredientsSalesExcelExportParams = IngredientsSalesTableParams & {
  language?: string
}

type SalesPdfExportParams = {
  startDate: string
  endDate: string
  language?: string
  limit?: number
  itemFilter?: 'dish' | 'topping' | 'danie' | 'dodatek' | null
}

type InventoryPdfExportParams = {
  startDate: string
  endDate: string
  language?: string
  limit?: number
  typeFilter?:
    | 'count'
    | 'weight'
    | 'volume'
    | 'ilość'
    | 'waga'
    | 'objętość'
    | null
}

// ── Daily items ──

type DailySalesItem = {
  date: string
  totalSales: number
}

type DailySalesItemWithProfit = {
  date: string
  totalSales: number
  profit: number
  cost: number
}

type DailySalesItemCount = {
  date: string
  totalCount: number
  trend: number
}

type DailySalesItemCountGranulated = {
  date: string
  totalCount: number
}

type DailyOrderAverage = {
  date: string
  averageOrderValue: number
}

type DailySalesItemAverageComparison = {
  date: string
  averageDaily: number
}

// ── Response models ──

type TotalSalesResponseWithProfit = {
  totalSales: number
  growthTrend: number
  dailySales: DailySalesItemWithProfit[]
}

type TotalSalesResponseCount = {
  dailyCounts: DailySalesItemCount[]
}

type TotalSalesResponseCountGranulated = {
  dailyCounts: DailySalesItemCountGranulated[]
  totalSalesCounts: number
  totalSalesCountsCompare: number
  comparisonPercentage: number
}

type TotalSalesResponseAverage = {
  averageOrderValuePeriod: number
  highestOrderValuePeriod: number
  totalOrders: number
  totalSales: number
  dailiesAverages: DailyOrderAverage[]
}

type TotalSalesResponseComparison = {
  totalSales: number
  dailySales: DailySalesItem[]
  totalSalesCompare: number
  comparisonPercentage: number
}

type TotalSalesResponseAverageComparison = {
  averageTotal: number
  comparisonPercentage: number
  averageTotalComparison: number
  dailiesAverages: DailySalesItemAverageComparison[]
}

type TotalSalesResponseComparisonCumulative = {
  cumulativeSales: DailySalesItem[]
  growthTrendComparison: number
}

// ── Top units ──

type DishItem = {
  name: string
  units: number
  percentage: number
  uuid: string
}

type DishToppingItem = {
  dishName: string
  toppingName: string
  units: number
  percentage: number
  toppingId: string
  dishId: string
}

type TopUnitsResponse = {
  tops: Record<string, DishItem[]>
}

type TopDishToppingResponse = {
  tops: Record<string, DishToppingItem[]>
}

// ── Sales table ──

type SalesRowMultiLang = {
  translations: Record<string, string>
  price: number
  quantity: number
  percentage: number
  translationsType: Record<string, string>
  itemId?: string | null
}

type SalesTableMultiLang = {
  totalSalesAmount: number
  totalSalesCount: number
  translationCoverage: Record<string, number>
  items: SalesRowMultiLang[]
}

// ── Translation ──

type TranslationEntry = {
  languageCode: string
  value?: string | null
}

type TranslationItem = {
  inputName: string
  translations: TranslationEntry[]
}

// ── Chatbot / Misc ──

type InstructionRequest = {
  query: string
}

type OpinionAnalysisRequest = {
  opinion: string
}

type OpinionItem = {
  topic: string
  sentiment: string
}

type OpinionAnalysisResult = {
  opinions: OpinionItem[]
}

type MenuItem = {
  name: string
  price: number | null
  category: string | null
  ingredients: string | null
  allergen: string | null
}

type MenuResponse = {
  items: MenuItem[]
}

// ── Query keys ──

enum QUERY {
  TOTAL_SALES_COUNT_GENERAL = 'totalSalesCountGeneral',
  TOTAL_SALES_VALUE_AVERAGE_GENERAL = 'totalSalesValueAverageGeneral',
  TOTAL_SALES_VALUE_GENERAL = 'totalSalesValueGeneral',
  TOP_BY_UNITS_TOPPINGS = 'topByUnitsToppings',
  TOP_BY_UNITS_DISHES = 'topByUnitsDishes',
  TOP_BY_UNITS_INGREDIENTS_MV = 'topByUnitsIngredientsMV',
  TOP_BY_UNITS_DISH_TOPPINGS = 'topByUnitsDishToppings',
  TOTAL_SALES_COUNT_GENERAL_REPORT = 'totalSalesCountGeneralReport',
  TOTAL_SALES_VALUE_AVERAGE_GENERAL_REPORT = 'totalSalesValueAverageGeneralReport',
  TOTAL_SALES_VALUE_GENERAL_REPORT = 'totalSalesValueGeneralReport',
  TOP_BY_UNITS_TOPPINGS_REPORT = 'topByUnitsToppingsReport',
  TOP_BY_UNITS_DISHES_REPORT = 'topByUnitsDishesReport',
  TOP_BY_UNITS_INGREDIENTS_MV_REPORT = 'topByUnitsIngredientsMVReport',
  TOP_BY_UNITS_DISH_TOPPINGS_REPORT = 'topByUnitsDishToppingsReport',
  GENERAL_FULL_REPORT = 'generalFullReport',
  TOTAL_SALES_VALUE_DISHES = 'totalSalesValueDishes',
  TOTAL_SALES_COUNT_DISHES = 'totalSalesCountDishes',
  TOTAL_SALES_AVERAGES_DISHES = 'totalSalesAveragesDishes',
  TOTAL_SALES_CUMULATIVE_VALUE_DISHES = 'totalSalesCumulativeValueDishes',
  TOTAL_SALES_VALUE_DISHES_REPORT = 'totalSalesValueDishesReport',
  TOTAL_SALES_COUNT_DISHES_REPORT = 'totalSalesCountDishesReport',
  TOTAL_SALES_AVERAGES_DISHES_REPORT = 'totalSalesAveragesDishesReport',
  TOTAL_SALES_CUMULATIVE_VALUE_DISHES_REPORT = 'totalSalesCumulativeValueDishesReport',
  DISHES_FULL_REPORT = 'dishesFullReport',
  TOTAL_SALES_VALUE_TOPPINGS = 'totalSalesValueToppings',
  TOTAL_SALES_COUNT_TOPPINGS = 'totalSalesCountToppings',
  TOTAL_SALES_AVERAGES_TOPPINGS = 'totalSalesAveragesToppings',
  TOTAL_SALES_CUMULATIVE_VALUE_TOPPINGS = 'totalSalesCumulativeValueToppings',
  TOTAL_SALES_VALUE_TOPPINGS_REPORT = 'totalSalesValueToppingsReport',
  TOTAL_SALES_COUNT_TOPPINGS_REPORT = 'totalSalesCountToppingsReport',
  TOTAL_SALES_AVERAGES_TOPPINGS_REPORT = 'totalSalesAveragesToppingsReport',
  TOTAL_SALES_CUMULATIVE_VALUE_TOPPINGS_REPORT = 'totalSalesCumulativeValueToppingsReport',
  TOPPINGS_FULL_REPORT = 'toppingsFullReport',
  TOTAL_SALES_COUNT_INGREDIENTS = 'totalSalesCountIngredients',
  TOTAL_SALES_VALUE_INGREDIENTS = 'totalSalesValueIngredients',
  TOTAL_SALES_AVERAGES_INGREDIENTS = 'totalSalesAveragesIngredients',
  TOTAL_SALES_CUMULATIVE_VALUE_INGREDIENTS = 'totalSalesCumulativeValueIngredients',
  TOTAL_SALES_COUNT_INGREDIENTS_REPORT = 'totalSalesCountIngredientsReport',
  TOTAL_SALES_VALUE_INGREDIENTS_REPORT = 'totalSalesValueIngredientsReport',
  TOTAL_SALES_AVERAGES_INGREDIENTS_REPORT = 'totalSalesAveragesIngredientsReport',
  TOTAL_SALES_CUMULATIVE_VALUE_INGREDIENTS_REPORT = 'totalSalesCumulativeValueIngredientsReport',
  INGREDIENTS_FULL_REPORT = 'ingredientsFullReport',
  MENU_ITEMS_SALES_TABLE = 'menuItemsSalesTable',
  INGREDIENTS_SALES_TABLE = 'ingredientsSalesTable',
  MENU_ITEMS_SALES_EXCEL = 'menuItemsSalesExcel',
  INGREDIENTS_SALES_EXCEL = 'ingredientsSalesExcel',
  DISHES_SALES_PDF = 'dishesSalesPdf',
  INVENTORY_PDF = 'inventoryPdf'
}

export { QUERY }
export type {
  DateRangeParams,
  ComparisonDateRangeParams,
  TopUnitsParams,
  TopUnitsIngredientsMvParams,
  IngredientComparisonParams,
  ReportParams,
  IngredientReportParams,
  FullReportParams,
  IngredientsFullReportParams,
  GeneralFullReportParams,
  SalesTableParams,
  IngredientsSalesTableParams,
  SalesExcelExportParams,
  IngredientsSalesExcelExportParams,
  SalesPdfExportParams,
  InventoryPdfExportParams,
  DailySalesItem,
  DailySalesItemWithProfit,
  DailySalesItemCount,
  DailySalesItemCountGranulated,
  DailyOrderAverage,
  DailySalesItemAverageComparison,
  TotalSalesResponseWithProfit,
  TotalSalesResponseCount,
  TotalSalesResponseCountGranulated,
  TotalSalesResponseAverage,
  TotalSalesResponseComparison,
  TotalSalesResponseAverageComparison,
  TotalSalesResponseComparisonCumulative,
  DishItem,
  DishToppingItem,
  TopUnitsResponse,
  TopDishToppingResponse,
  SalesRowMultiLang,
  SalesTableMultiLang,
  TranslationEntry,
  TranslationItem,
  InstructionRequest,
  OpinionAnalysisRequest,
  OpinionAnalysisResult,
  MenuItem,
  MenuResponse
}
